/* Instrumentene loggen og fôringssiden deler.
 *
 * Flyttet hit fra SiteLog framfor kopiert, av den grunnen `phoniq-labeler`
 * dokumenterer og lever med: to kopier av samme figur driver fra hverandre, og
 * to sider som tegner den samme ringen litt ulikt er verre enn én side.
 *
 * Ringen tar målet som parameter. Hvile har 80 og fôring har 95, og de er ikke
 * det samme spørsmålet: 80 av 100 er «rolig nok til å telle», 95 er «fôret som
 * vanlig». Et hardkodet mål her ville gjort den ene siden til en løgn. */

import { useEffect, useRef, useState } from 'react'

/** Teller opp til tallet når det endrer seg.
 *
 *  Ikke pynt: tallet og ringen skal bevege seg SAMMEN. Når ringen tweenes og
 *  sifferet hopper, leser øyet dem som to ting, og den ene bevegelsen siden har
 *  mistet poenget sitt. Av under `prefers-reduced-motion`, som resten. */
export function useTellOpp(maal: number | null, ms = 900): number | null {
  const [vis, setVis] = useState(maal)
  const fra = useRef(maal)

  useEffect(() => {
    if (maal === null) { setVis(null); return }
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    const start = fra.current
    if (rolig || start === null) { setVis(maal); fra.current = maal; return }

    let raf = 0
    const t0 = performance.now()
    const steg = (naa: number) => {
      const p = Math.min(1, (naa - t0) / ms)
      // Samme kurve som --ease, så tall og ring følger hverandre.
      const e = 1 - Math.pow(1 - p, 3)
      setVis(start + (maal - start) * e)
      if (p < 1) raf = requestAnimationFrame(steg)
      else fra.current = maal
    }
    raf = requestAnimationFrame(steg)
    return () => cancelAnimationFrame(raf)
  }, [maal, ms])

  return vis
}

/** Ringen. Alltid samme farge, alltid samme tykkelse.
 *
 *  En dårlig verdi er en KORT ring, ikke en rød en. Fargen forteller hva slags
 *  side du ser på; lengden forteller hvordan det gikk. */
export function Ring({ score, maal }: { score: number | null; maal: number }) {
  const [tegnet, setTegnet] = useState(false)
  const r = 54
  const omkrets = 2 * Math.PI * r
  const naadd = score !== null && score >= maal

  /* Bare én gang. Å nullstille denne på hver scoreendring ville fått ringen
     til å tømme seg og fylle seg igjen hver gang du bytter dag -- en
     bevegelse som sier «noe skjedde» der det riktige er «det gled hit». */
  useEffect(() => {
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (rolig) { setTegnet(true); return }
    const id = requestAnimationFrame(() => setTegnet(true))
    return () => cancelAnimationFrame(id)
  }, [])

  const andel = score === null ? 0 : Math.max(0, Math.min(1, score / 100))

  /* Ingen -90 her. `.ring` bærer allerede `rotate(-90deg)` i CSS -- det er det
     som flytter buens start fra klokka tre til klokka tolv -- så et hakk som
     trakk fra 90 selv ble rotert to ganger og havnet nederst. */
  const vinkel = (maal / 100) * 360

  return (
    <svg className={`ring${naadd ? ' naadd' : ''}`} viewBox="0 0 128 128"
         aria-hidden="true">
      <circle className="ring-spor" cx="64" cy="64" r={r} />
      <line className="ring-maal"
            x1={64 + (r - 5) * Math.cos((vinkel * Math.PI) / 180)}
            y1={64 + (r - 5) * Math.sin((vinkel * Math.PI) / 180)}
            x2={64 + (r + 5) * Math.cos((vinkel * Math.PI) / 180)}
            y2={64 + (r + 5) * Math.sin((vinkel * Math.PI) / 180)} />
      <circle className="ring-verdi" cx="64" cy="64" r={r}
        strokeDasharray={omkrets}
        strokeDashoffset={omkrets * (1 - (tegnet ? andel : 0))} />
    </svg>
  )
}

/** Ett nøkkeltall. Med `aapne` er det også en dør inn til der tallet kom fra. */
export function Noekkel({ verdi, enhet, merke, varsel, aapne }: {
  verdi: string; enhet?: string; merke: string; varsel?: boolean
  aapne?: () => void
}) {
  const inner = (
    <>
      <span className={`noekkel-verdi${varsel ? ' varsel' : ''}`}>
        {verdi}{enhet && <small>{enhet}</small>}
      </span>
      <span className="noekkel-merke">{merke}</span>
    </>
  )
  if (!aapne) return <div className="noekkel-en">{inner}</div>
  return <button className="noekkel-en aapner" onClick={aapne}>{inner}</button>
}

/** Om brukeren har bedt om mindre bevegelse.
 *
 *  Leses i JS og ikke bare i CSS fordi SMIL i SVG ikke bryr seg om
 *  `prefers-reduced-motion`: en `<animate>` som er rendret, går. Den må ikke
 *  rendres i det hele tatt. */
export function useRolig(): boolean {
  const [rolig] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
  return rolig
}

/** Sant først etter første frame, så en CSS-overgang har noe å gå fra.
 *
 *  Av under `prefers-reduced-motion`: da settes den med én gang, og elementet
 *  står ferdig ved første maling framfor å animere raskt. */
export function useInne(deps: unknown[] = []): boolean {
  const [inne, setInne] = useState(false)
  useEffect(() => {
    const rolig = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (rolig) { setInne(true); return }
    const id = requestAnimationFrame(() => setInne(true))
    return () => cancelAnimationFrame(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return inne
}
